import {isPlainObject} from '../../../utils/isPlainObject';
import type {ApiErrorKind} from './types';

export class ApiError extends Error {
  kind: ApiErrorKind;
  status?: number;
  code?: number;
  details?: unknown;

  constructor(message: string, init: {kind?: ApiErrorKind; status?: number; code?: number; details?: unknown}) {
    super(message);
    this.name = 'ApiError';
    this.kind = init.kind ?? 'unknown';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
  }
}

export const normalizeError = (
  error: unknown,
  fallbackMessage = 'Unexpected API error',
  kind: ApiErrorKind = 'unknown'
): ApiError => {
  if (error instanceof ApiError) {
    return error;
  }

  if (isPlainObject(error)) {
    const obj = error;

    if ('message' in obj || 'error' in obj || 'errorCode' in obj) {
      const rawStatus = obj.status;
      const parsedStatus = typeof rawStatus === 'string' || typeof rawStatus === 'number' ? Number(rawStatus) : undefined;
      const status = Number.isFinite(parsedStatus) ? parsedStatus : undefined;

      const getRawCode = () => {
        if ('code' in obj) {
          return obj.code;
        }
        if ('errorCode' in obj) {
          return obj.errorCode;
        }
        return undefined;
      };

      const rawCode = getRawCode();
      const parsedCode = typeof rawCode === 'string' || typeof rawCode === 'number' ? Number(rawCode) : undefined;
      const code = Number.isFinite(parsedCode) ? parsedCode : undefined;

      const messageFromMessage =
        typeof obj.message === 'string' && obj.message.trim().length > 0 ? obj.message : undefined;
      const messageFromError = typeof obj.error === 'string' && obj.error.trim().length > 0 ? obj.error : undefined;
      const message = messageFromMessage ?? messageFromError ?? fallbackMessage;

      return new ApiError(message, {
        kind,
        code,
        status,
        details: {
          raw: obj,
          rpcData: 'data' in obj ? obj.data : undefined,
        },
      });
    }
  }

  if (error instanceof Error) {
    return new ApiError(error.message || fallbackMessage, {kind, details: {cause: error}});
  }

  return new ApiError(fallbackMessage, {kind, details: {raw: error}});
};
